import { ChangeDetectionStrategy, Component, computed, inject, input, resource } from '@angular/core';
import { CurrencyPipe, DatePipe, DecimalPipe } from '@angular/common';
import { RouterLink } from '@angular/router';
import { AvatarComponent } from '../../../shared/components/avatar/avatar.component';
import { CurrentUserService } from '../../../shared/services/current-user.service';
import { OrderListComponent } from '../../orders/order-list/order-list.component';
import { CustomerApi } from '../customer-api';
import { sizesByOrders } from '../customer.utils';

/** Read-only details of one customer of the current company: customers/:id. */
@Component({
  selector: 'app-customer-details',
  imports: [AvatarComponent, CurrencyPipe, DatePipe, DecimalPipe, OrderListComponent, RouterLink],
  templateUrl: './customer-details.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block h-full' },
})
export class CustomerDetails {
  readonly #customerApi = inject(CustomerApi);
  readonly #currentUser = inject(CurrentUserService);

  /** Route param, the customer's user id. */
  readonly id = input.required<string>();

  readonly #companyId = computed(() => this.#currentUser.company()?.companyId);

  /** undefined value means the customer does not exist in this company. */
  protected readonly customer = resource({
    params: () => {
      const companyId = this.#companyId();
      return companyId ? { companyId, customerId: this.id() } : undefined;
    },
    loader: ({ params }) => this.#customerApi.get(params.companyId, params.customerId),
  });

  readonly #loaded = computed(() => (this.customer.hasValue() ? this.customer.value() : undefined));

  protected readonly spent = computed(() => (this.#loaded()?.totalSpentCents ?? 0) / 100);
  protected readonly averageOrder = computed(() => {
    const customer = this.#loaded();
    return customer?.orderCount ? customer.totalSpentCents / customer.orderCount / 100 : null;
  });
  protected readonly sizes = computed(() => {
    const customer = this.#loaded();
    return customer ? sizesByOrders(customer.bySize) : [];
  });
}
